/**
 *  @module Resources/Charges
 */

import { Connector } from "checkout/ts/Connector";
import { isTest } from "checkout/ts/utils/environment";
import { SupportedLocale } from "common/locale/labels";
import { isCI, isDevelop } from "common/utils/environment";
import {
    AuthParams,
    ChargeItem,
    Charges as NodeCharges,
    ChargeStatus,
    DefinedRoute,
    HTTPMethod,
    PaymentType,
    ResponseErrorCode,
    SendData,
    TransactionTokenItem,
} from "univapay-node";

export type ChargeExpiry = {
    id: string;
    chargeId: string;
    expirationDate: string;
    extensionCount: number;
    createdOn: string;
    updatedOn: string;
};

type IssuerToken = {
    issuerToken: string;
    callMethod: "http_post" | "http_get";
    payload: Record<string, string>;
    paymentType: PaymentType;
    contentType: string;
};

export enum ChargeThreeDsMode {
    NORMAL = "normal", // default
    FORCE = "force",
    SKIP = "skip",
    IF_AVAILABLE = "if_available",
    REQUIRE = "require",
    PROVIDED = "provided",
}

export type PatchedChargeItem = ChargeItem & {
    threeDs?: {
        mode?: ChargeThreeDsMode;
        redirectEndpoint?: string;
        redirectId?: string;
    };
};

export class Charges extends NodeCharges {
    private _expiry?: DefinedRoute;
    expiry(storeId: string, chargeId: string, data?: SendData<void>, auth?: AuthParams): Promise<ChargeExpiry> {
        this._expiry =
            this._expiry ?? this.defineRoute(HTTPMethod.GET, "/stores/:storeId/charges/:chargeId/expiry/latest");
        return this._expiry(data, undefined, auth, { storeId, chargeId });
    }

    private _issuerToken?: DefinedRoute;
    issuerToken(storeId: string, chargeId: string, auth?: AuthParams): Promise<IssuerToken> {
        this._issuerToken =
            this._issuerToken ??
            this.defineRoute(
                HTTPMethod.GET,
                "/stores/:storeId/charges/:chargeId/three_ds/issuer_token",
                undefined,
                undefined,
                undefined,
                undefined,
                ["payload"]
            );
        return this._issuerToken(null, null, auth, { storeId, chargeId });
    }

    async pollThreeDs(
        storeId: string,
        chargeId: string,
        locale: SupportedLocale,
        connector: Connector,
        token?: TransactionTokenItem
    ) {
        connector.emitter.emit("checkout:three-ds-authorization-open-modal", {
            storeId,
            chargeId,
            jwt: this.api.jwtRaw,
            endpoint: this.api.endpoint,
            locale,
        });

        const charge = await this.poll(
            storeId,
            chargeId,
            null,
            null,
            null,
            null,
            (charge: PatchedChargeItem) =>
                ![ChargeStatus.PENDING, ChargeStatus.AWAITING].includes(charge?.status) || isTest()
        );

        // Add a timeout to show the redirect page
        if (isDevelop() && !isCI()) {
            await new Promise((resolve) => setTimeout(() => resolve(""), 2000));
        }

        return isTest()
            ? {
                  ...charge,
                  status: token.email.includes("+3ds-failure") ? ChargeStatus.ERROR : ChargeStatus.SUCCESSFUL,
                  error: token.email.includes("+3ds-failure")
                      ? { code: 400, message: ResponseErrorCode.CardInvalid }
                      : undefined,
              }
            : charge;
    }
}
